/** Content for the Help Center — contact routes, FAQs, tutorials and the legal pages. */

export interface Faq {
    question: string;
    answer: string;
}

export interface Tutorial {
    slug: string;
    title: string;
    duration: string;
    thumbnail: string;
    /** Embed URL. Absent until one is attached in the CMS. */
    videoUrl?: string;
}

const DEFAULT_INTRO =
    'Answers to the questions we are asked most, walkthroughs of the tools, and a way to reach a person when none of that helps. If you have a live legal matter, use the legal aid form rather than this page.';

/** Held in an object so the CMS can replace it at startup. */
export const HELP_INTRO_REF = { text: DEFAULT_INTRO };

export const CONTACT = {
    heading: 'You still have a question?',
    body: 'If you cannot find your question in our FAQs, contact us and we will answer shortly.',
    phone: '+234 (0) 800 000 0000',
    phoneNote: 'Monday to Friday, 9am–5pm WAT',
    email: 'help@citizensgavel.org',
    emailNote: 'Best way to get a written answer',
};

export const FAQS: Faq[] = [
    {
        question: 'What does Citizens’ Gavel actually do?',
        answer: 'We move stalled cases. Our duty solicitors take on files that have stopped progressing, we publish court performance data that makes delay visible, and we build tools that carry legal help to people who would not otherwise reach a lawyer. Everything is free to the person asking.',
    },
    {
        question: 'Is legal aid from Gavel free?',
        answer: 'Yes. There is no fee at any stage and you do not need a referral. If we cannot take your matter ourselves, we will tell you why and point you to who can.',
    },
    {
        question: 'How long will it take before someone contacts me?',
        answer: 'A caseworker reads every request. Where someone is in custody we aim to make contact within two working days; other matters usually take under a week. Say so in the form if someone is being held.',
    },
    {
        question: 'Is Podus AI a substitute for a lawyer?',
        answer: 'No, and it will tell you so. It answers questions in plain language and helps you understand a document, but it does not give legal advice, predict outcomes or file anything on your behalf. Anything that needs counsel is handed to counsel.',
    },
    {
        question: 'What happens to the information I send you?',
        answer: 'Legal aid requests are restricted to administrators, and every time a member of staff reveals a contact detail it is recorded against their account. Requests are retained for 90 days from triage and then deleted. We never sell or share your details.',
    },
    {
        question: 'Can I volunteer without a law degree?',
        answer: 'Yes. Lawyers take on pro bono files, but most of the work is research, translation, court observation, outreach and data. Tell us where you can help and how much time you have.',
    },
    {
        question: 'Where does your court data come from?',
        answer: 'From the registries themselves, through freedom of information requests and direct partnerships. Where a court declined to publish, we say so rather than filling the gap with an estimate.',
    },
];

export const TUTORIALS: Tutorial[] = [
    {
        slug: 'request-legal-aid',
        title: 'How to request legal aid',
        duration: '4:12',
        thumbnail: '/landing/stories/story1.webp',
    },
    {
        slug: 'reading-justice-clock',
        title: 'Reading the Justice Clock dashboard',
        duration: '6:41',
        thumbnail: '/landing/justice-performance/skyline.webp',
    },
    {
        slug: 'using-podus-ai',
        title: 'Asking Podus AI a legal question',
        duration: '3:52',
        thumbnail: '/landing/video/gallery-mid-top.webp',
    },
    {
        slug: 'court-rankings',
        title: 'Finding your state in the court rankings',
        duration: '5:20',
        thumbnail: '/landing/video/gallery-right.webp',
    },
];

/** Long-form legal pages, stored as paragraphs. */
export interface LegalDocument {
    title: string;
    updated: string;
    sections: { heading: string; paragraphs: string[] }[];
}

export const PRIVACY_POLICY: LegalDocument = {
    title: 'Privacy Policy',
    updated: '12 August 2026',
    sections: [
        {
            heading: 'What we collect',
            paragraphs: [
                'We collect only what a request needs. A legal aid request asks for your name, a way to contact you, and an account of what happened. A volunteer sign-up asks for your name, contact details and where you would like to help. The newsletter asks for an email address and nothing else.',
                'We do not require an account to use this site, and we do not ask for identity documents, financial details or anything about your health unless it forms part of the matter you are describing to us.',
            ],
        },
        {
            heading: 'How we use it',
            paragraphs: [
                'Information you send through a form is used to answer you and, where relevant, to act on the matter you raised. It is not used for advertising, it is not sold, and it is not shared with third parties except where we are instructing a lawyer on your behalf or where a court or the law requires it.',
                'We publish research and case patterns in aggregate. Nothing we publish identifies a person who came to us for help unless they have given consent in writing, separately from the request itself.',
            ],
        },
        {
            heading: 'Legal aid requests',
            paragraphs: [
                'Requests for legal aid are treated as sensitive from the moment they arrive. They frequently concern someone in detention. Access is restricted to administrators, contact details are masked until a member of staff deliberately reveals them, and every reveal is written to an audit log against that person’s account.',
                'Legal aid requests are retained for 90 days from the point of triage and are then deleted, unless the matter is still live or we are required to keep them longer.',
            ],
        },
        {
            heading: 'Cookies',
            paragraphs: [
                'We set only strictly necessary cookies unless you tell us otherwise. Preference, statistics and marketing cookies are off by default and run only if you turn them on. You can change or withdraw that choice at any time from the cookie settings link in the footer, and withdrawing is as easy as giving it.',
            ],
        },
        {
            heading: 'Your rights',
            paragraphs: [
                'You can ask us what we hold about you, ask us to correct it, or ask us to delete it. Write to the address below and we will respond within 30 days. If you are unhappy with how we have handled your information, you may complain to the Nigeria Data Protection Commission.',
                'Contact: help@citizensgavel.org',
            ],
        },
    ],
};

export const TERMS: LegalDocument = {
    title: 'Terms & Conditions',
    updated: '12 August 2026',
    sections: [
        {
            heading: 'What this site is',
            paragraphs: [
                'This site is published by Citizens’ Gavel, a civic-technology organisation. It provides information about our work, access to our research, and forms through which you can ask for help or offer it.',
            ],
        },
        {
            heading: 'This is not legal advice',
            paragraphs: [
                'Nothing on this site is legal advice, and reading it does not create a lawyer–client relationship. Our guidance, our tools and anything Podus AI tells you are general information. A lawyer–client relationship begins only when we have accepted your matter and told you so in writing.',
                'If you are facing a deadline or someone is in custody, do not rely on this site alone. Use the legal aid form and say clearly that the matter is urgent.',
            ],
        },
        {
            heading: 'Using our research',
            paragraphs: [
                'Our reports, publications and court data are free to read, quote and republish with attribution. We ask that you do not present our figures as your own, and that you preserve the caveats — particularly where a court declined to publish and a figure is an estimate rather than a return.',
            ],
        },
        {
            heading: 'What we ask of you',
            paragraphs: [
                'Do not use our forms to send material that is unlawful, or to send information about another person without a good reason to do so. Do not attempt to access parts of this site you have not been given access to.',
            ],
        },
        {
            heading: 'Availability',
            paragraphs: [
                'We do our best to keep this site and our tools running, but we do not guarantee uninterrupted availability. We may change or withdraw a feature, and we will say so where the change affects how you reach help.',
            ],
        },
    ],
};
